import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageCircle, Send, RefreshCw, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Flag, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { sentenceQaApi } from '../../utils/api';
import { toast } from 'sonner';

const POLL_INTERVAL_MS = 2500;
const MAX_POLLS = 20; // 50s max

// ── Single QA item ────────────────────────────────────────────────────────────

function QAItem({ item, onRetry }) {
  const [expanded, setExpanded] = useState(true);
  const { question, answer } = item;
  const isPending = question.status === 'pending' || question.status === 'generating';
  const isFailed = question.status === 'failed';

  return (
    <div className="border border-border/50 rounded-lg overflow-hidden" data-testid="qa-item">
      {/* Question header */}
      <button
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <MessageCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/70" />
        <span className="flex-1 text-sm font-medium leading-snug">{question.question_text}</span>
        {expanded ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </button>

      {/* Answer area */}
      {expanded && (
        <div className="px-3 pb-3 pt-0 ml-5 text-sm text-muted-foreground">
          {isPending && (
            <div className="flex items-center gap-2 text-xs italic animate-pulse" data-testid="qa-generating">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Generating explanation…
            </div>
          )}
          {isFailed && (
            <div className="flex items-center gap-2 text-xs text-destructive" data-testid="qa-failed">
              Failed to generate.{' '}
              <button className="underline hover:no-underline" onClick={() => onRetry?.(question)} data-testid="qa-retry-btn">
                Retry
              </button>
            </div>
          )}
          {answer && (
            <p className="leading-relaxed whitespace-pre-wrap" data-testid="qa-answer-text">
              {answer.answer_text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export default function SentenceQAPanel({ open, onClose, textId, sentenceIndex, sentenceText, languageCode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 10;
  const [canonicalExp, setCanonicalExp] = useState(null);
  const [canonVote, setCanonVote] = useState(0); // user's current vote: +1 / -1 / 0
  const [canonVoting, setCanonVoting] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportText, setReportText] = useState('');
  const [relatedExps, setRelatedExps] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const pollTimers = useRef({});

  // Check feature flag on mount
  useEffect(() => {
    sentenceQaApi.getStatus()
      .then(r => setEnabled(r.data?.questions === true))
      .catch(() => setEnabled(false));
  }, []);

  // Load questions when panel opens or sentence changes
  useEffect(() => {
    if (!open || !enabled || textId == null || sentenceIndex == null) return;
    setItems([]);
    setOffset(0);
    setTotal(0);
    setCanonicalExp(null);
    setCanonVote(0);
    setRelatedExps([]);
    loadQuestions(0);
    // Also check for a published canonical explanation
    sentenceQaApi.getExplanation(textId, sentenceIndex)
      .then(r => {
        setCanonicalExp(r.data?.explanation || null);
      })
      .catch(() => {});
    // S27: load a few related explanations for this language
    if (languageCode) {
      sentenceQaApi.getLibrary({ language_code: languageCode, limit: 3, sort: 'score' })
        .then(r => setRelatedExps(r.data?.items || []))
        .catch(() => {});
    }
  }, [open, textId, sentenceIndex, enabled]); // eslint-disable-line

  // Clean up polls on unmount
  useEffect(() => {
    return () => Object.values(pollTimers.current).forEach(clearInterval);
  }, []);

  const loadQuestions = async (off = 0) => {
    setLoading(true);
    try {
      const res = await sentenceQaApi.listQuestions(textId, sentenceIndex, { limit: PAGE_SIZE, offset: off });
      const rawItems = res.data?.items || [];
      setTotal(res.data?.total || 0);
      setOffset(off + rawItems.length);
      setItems(prev => off === 0 ? rawItems : [...prev, ...rawItems]);
      rawItems.forEach(item => {
        if (item.question.status === 'pending' || item.question.status === 'generating') {
          startPolling(item.question.question_id);
        }
      });
    } catch (e) {
      console.error('SentenceQAPanel: failed to load', e);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = useCallback((questionId) => {
    if (pollTimers.current[questionId]) return;
    let count = 0;
    pollTimers.current[questionId] = setInterval(async () => {
      count++;
      if (count > MAX_POLLS) {
        clearInterval(pollTimers.current[questionId]);
        delete pollTimers.current[questionId];
        return;
      }
      try {
        const res = await sentenceQaApi.pollQuestion(questionId);
        const { question, answer } = res.data;
        if (question.status === 'answered' || question.status === 'failed') {
          clearInterval(pollTimers.current[questionId]);
          delete pollTimers.current[questionId];
        }
        setItems(prev => prev.map(item =>
          item.question.question_id === questionId
            ? { question, answer }
            : item
        ));
      } catch (_) {}
    }, POLL_INTERVAL_MS);
  }, []);

  const handleRetry = useCallback(async (question) => {
    try {
      await sentenceQaApi.regenerate(question.question_id);
      setItems(prev => prev.map(item =>
        item.question.question_id === question.question_id
          ? { ...item, question: { ...item.question, status: 'pending' } }
          : item
      ));
      startPolling(question.question_id);
    } catch (e) {
      console.error('SentenceQAPanel: retry failed', e);
    }
  }, [startPolling]);

  const handleVote = useCallback(async (vote) => {
    if (!canonicalExp) return;
    const newVote = canonVote === vote ? 0 : vote; // toggle off if same vote
    setCanonVoting(true);
    try {
      await sentenceQaApi.voteExplanation(canonicalExp.explanation_id, newVote === 0 ? (vote === 1 ? -1 : 1) : newVote);
      setCanonVote(newVote);
      toast.success(newVote === 1 ? 'Marked as helpful' : newVote === -1 ? 'Marked as not helpful' : 'Vote removed');
    } catch (e) {
      toast.error('Could not record vote');
    } finally {
      setCanonVoting(false);
    }
  }, [canonicalExp, canonVote]);

  const handleReport = useCallback(async () => {
    if (!canonicalExp || !reportText.trim()) return;
    try {
      await sentenceQaApi.reportExplanation(canonicalExp.explanation_id, reportText.trim());
      toast.success('Reported — thank you');
      setShowReport(false);
      setReportText('');
    } catch (e) {
      toast.error('Could not submit report');
    }
  }, [canonicalExp, reportText]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = questionText.trim();
    if (!q) return;
    setSubmitting(true);
    try {
      const res = await sentenceQaApi.submitQuestion(textId, sentenceIndex, {
        question_text: q,
        surface_sentence: sentenceText,
        language_code: languageCode,
      });
      const newItem = { question: res.data, answer: null };
      setItems(prev => [newItem, ...prev]);
      setQuestionText('');
      startPolling(res.data.question_id);
    } catch (e) {
      console.error('SentenceQAPanel: submit failed', e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0 p-0" data-testid="sentence-qa-panel">
        <SheetHeader className="px-4 py-3 border-b border-border/50 shrink-0">
          <SheetTitle className="text-sm font-semibold flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-primary" />
            Ask about this sentence
          </SheetTitle>
          {sentenceText && (
            <p className="text-xs text-muted-foreground italic leading-snug mt-1 line-clamp-3" data-testid="qa-panel-sentence">
              &ldquo;{sentenceText}&rdquo;
            </p>
          )}
        </SheetHeader>

        {!enabled ? (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-4 text-center" data-testid="qa-feature-disabled">
            This feature is not enabled yet.
          </div>
        ) : (
          <>
            {/* Canonical explanation banner (S21) */}
            {canonicalExp && (
              <div className="mx-4 mt-3 p-3 rounded-lg bg-primary/8 border border-primary/20 text-sm" data-testid="canonical-explanation">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-primary/80 uppercase tracking-wide">
                    <MessageCircle className="h-3 w-3" /> Community Answer
                  </div>
                  {/* S23 vote + report */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleVote(1)}
                      disabled={canonVoting}
                      className={`p-1 rounded hover:bg-muted/40 transition-colors ${canonVote === 1 ? 'text-green-500' : 'text-muted-foreground'}`}
                      title="Helpful"
                      data-testid="canon-vote-up"
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleVote(-1)}
                      disabled={canonVoting}
                      className={`p-1 rounded hover:bg-muted/40 transition-colors ${canonVote === -1 ? 'text-red-400' : 'text-muted-foreground'}`}
                      title="Not helpful"
                      data-testid="canon-vote-down"
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setShowReport(v => !v)}
                      className="p-1 rounded hover:bg-muted/40 transition-colors text-muted-foreground hover:text-destructive"
                      title="Report"
                      data-testid="canon-report-btn"
                    >
                      <Flag className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">{canonicalExp.explanation_text}</p>
                {/* Helpfulness score */}
                {(canonicalExp.helpfulness_up > 0 || canonicalExp.helpfulness_down > 0) && (
                  <p className="mt-1.5 text-[10px] text-muted-foreground">
                    {canonicalExp.helpfulness_up} helpful · {canonicalExp.helpfulness_down} not helpful
                  </p>
                )}
                {/* Report form */}
                {showReport && (
                  <div className="mt-2 pt-2 border-t border-border/30">
                    <Textarea
                      value={reportText}
                      onChange={e => setReportText(e.target.value)}
                      placeholder="Why are you reporting this? (optional)"
                      className="resize-none text-xs min-h-[48px]"
                      maxLength={300}
                      data-testid="report-reason-input"
                    />
                    <div className="flex gap-2 mt-1.5">
                      <Button size="sm" variant="destructive" className="text-xs h-6 px-2" onClick={handleReport} data-testid="report-submit-btn">Submit</Button>
                      <Button size="sm" variant="ghost" className="text-xs h-6 px-2" onClick={() => setShowReport(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Question form */}
            <form onSubmit={handleSubmit} className="px-4 py-3 border-b border-border/50 shrink-0">
              <Textarea
                value={questionText}
                onChange={e => setQuestionText(e.target.value)}
                placeholder="Ask about grammar, vocabulary, meaning…"
                className="resize-none text-sm min-h-[72px]"
                maxLength={500}
                data-testid="qa-question-input"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-muted-foreground">{questionText.length}/500</span>
                <Button type="submit" size="sm" disabled={!questionText.trim() || submitting} data-testid="qa-submit-btn">
                  {submitting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  <span className="ml-1">Ask</span>
                </Button>
              </div>
            </form>

            {/* Answers list */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="qa-list">
              {loading && <p className="text-xs text-muted-foreground text-center py-4">Loading…</p>}
              {!loading && items.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4" data-testid="qa-empty">
                  No questions yet. Ask anything about this sentence.
                </p>
              )}
              {items.map(item => (
                <QAItem key={item.question.question_id} item={item} onRetry={handleRetry} />
              ))}
              {items.length < total && (
                <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => loadQuestions(offset)} disabled={loading} data-testid="qa-load-more">
                  {loading ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : null}
                  Load more ({total - items.length} remaining)
                </Button>
              )}

              {/* S27 — Related explanations from the library */}
              {relatedExps.length > 0 && items.length === 0 && !loading && (
                <div className="mt-4 pt-3 border-t border-border/30" data-testid="related-explanations">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> From the explanation library
                  </p>
                  {relatedExps.map(exp => (
                    <div key={exp.explanation_id} className="mb-2 p-2.5 rounded-md bg-muted/20 border border-border/30">
                      {exp.context_sentence && (
                        <p className="text-[10px] text-muted-foreground italic mb-1 line-clamp-1">&ldquo;{exp.context_sentence}&rdquo;</p>
                      )}
                      <p className="text-xs leading-relaxed line-clamp-3">{exp.explanation_text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
